/**
 * @fileoverview Hook Data Layer para opciones de subcategoría de eventos de cliente (actividad).
 * Devuelve las opciones traducidas y renderizadas para el modal de actividad.
 * @module useCustomerEventSubcategoryOptions
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.0.0
 */

import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import LinkedinMessageIcon from 'images/new_design/linkedin_message.svg';
import CallIcon from 'images/new_design/call.svg';
import AnotherActivityIcon from 'images/new_design/another_activity.svg';
import MeetIcon from 'images/new_design/meet.svg';

/**
 * Hook Data Layer para obtener las opciones de subcategoría de evento de cliente.
 * Devuelve las opciones con label traducido y renderLabel.
 *
 * @returns {Array<{id: string, label: string, renderLabel: JSX.Element}>}
 *
 * @example
 * const options = useCustomerEventSubcategoryOptions();
 *
 * @see ManageCustomerEventModalMC
 */
export default function useCustomerEventSubcategoryOptions() {
  const { t } = useTranslation('InboxMC');

  // Renderizador reutilizable memoizado para cada opción
  const renderLabel = useCallback(
    (icon, translationKey) => (
      <div className="tw-flex tw-items-center">
        <img className="tw-w-8 tw-h-8 tw-mr-2" src={icon} alt="" />
        <span>{t(translationKey)}</span>
      </div>
    ),
    [t]
  );

  return useMemo(() => ([
    {
      id: 'call',
      label: t('event.call'),
      renderLabel: renderLabel(CallIcon, 'event.call')
    },
    {
      id: 'linkedin_message',
      label: t('event.linkedin_message'),
      renderLabel: renderLabel(LinkedinMessageIcon, 'event.linkedin_message')
    },
    {
      id: 'meet',
      label: t('event.meet'),
      renderLabel: renderLabel(MeetIcon, 'event.meet')
    },
    {
      id: 'another_activity',
      label: t('event.another_activity'),
      renderLabel: renderLabel(AnotherActivityIcon, 'event.another_activity')
    }
  ]), [t]);
}
